import api from './api';

const atlassianOAuthService = {
  /**
   * Get the authorization URL to start the OAuth flow
   * @param {string} returnTo - URL to redirect to after successful auth
   * @returns {Promise<{authorization_url: string, state: string}>}
   */
  async getAuthorizationUrl(returnTo = null) {
    const params = returnTo ? { return_to: returnTo } : {};
    const response = await api.get('/atlassian_oauth/authorize', { params });
    return response.data;
  },

  /**
   * Initiate OAuth flow by opening authorization URL in a popup
   * @param {string} returnTo - URL to redirect to after successful auth
   * @returns {Promise<void>}
   */
  async initiateOAuthFlow(returnTo = null) {
    try {
      const { authorization_url } = await this.getAuthorizationUrl(returnTo);
      
      // Open OAuth flow in a popup window
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        authorization_url,
        'Atlassian OAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      // Return a promise that resolves when the popup completes
      return new Promise((resolve, reject) => {
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            // Check if authorization was successful by checking status
            this.getStatus()
              .then(status => {
                if (status.authorized) {
                  resolve(status);
                } else {
                  reject(new Error('Authorization was cancelled or failed'));
                }
              })
              .catch(reject);
          }
        }, 1000);

        // Listen for messages from popup (if using postMessage)
        const messageHandler = (event) => {
          if (event.data.type === 'ATLASSIAN_OAUTH_SUCCESS') {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            popup.close();
            resolve(event.data);
          } else if (event.data.type === 'ATLASSIAN_OAUTH_ERROR') {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            popup.close();
            reject(new Error(event.data.error));
          }
        };

        window.addEventListener('message', messageHandler);
      });
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      throw error;
    }
  },

  /**
   * Get current OAuth status
   * @returns {Promise<{authorized: boolean, expires_at: string|null, scope: string|null}>}
   */
  async getStatus() {
    const response = await api.get('/atlassian_oauth/status');
    return response.data;
  },

  /**
   * Revoke OAuth token
   * @returns {Promise<void>}
   */
  async revoke() {
    await api.post('/atlassian_oauth/revoke');
  },

  /**
   * Get Rovo OAuth status
   * @returns {Promise<{server_accessible: boolean, has_token: boolean, is_rovo_token: boolean, ...}>}
   */
  async getRovoStatus() {
    return {
      disabled: true,
      has_token: false,
      is_rovo_token: false,
      server_accessible: false,
      message: 'Rovo MCP is temporarily disabled. Feature coming soon.',
    };
  },

  /**
   * Get the Rovo authorization URL to start the Rovo OAuth flow
   * @param {string} returnTo - URL to redirect to after successful auth
   * @returns {Promise<{authorization_url: string, state: string}>}
   */
  async getRovoAuthorizationUrl() {
    throw new Error('Rovo MCP is temporarily disabled. Feature coming soon.');
  },

  /**
   * Initiate Rovo OAuth flow by opening authorization URL in a popup
   * @param {string} returnTo - URL to redirect to after successful auth
   * @returns {Promise<void>}
   */
  async initiateRovoOAuthFlow() {
    throw new Error('Rovo MCP is temporarily disabled. Feature coming soon.');
  }
};

export default atlassianOAuthService;

